import { useEffect, useState } from "react";
import { ChevronDown, BookOpen, LifeBuoy } from "lucide-react";
import { api } from "../../lib/api";

export const HelpCenter = ({ onContactSupport }) => {
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/support/articles");
        setArticles(data.articles || []);
      } catch {
        setArticles([]);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="help-center-panel">
      <div className="border border-zinc-800 bg-zinc-900/40 p-6 md:p-8">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">
          <BookOpen className="w-3 h-3" /> Help center
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">
          Everything about getting subtitles out of LinguaSub
        </h2>
        <p className="text-sm text-zinc-400 mt-3 max-w-2xl leading-relaxed">
          Nine short guides covering the whole flow — upload, transcription,
          translation, timing, downloads, credits, and what to do when
          something misbehaves.
        </p>
      </div>

      <div className="border border-zinc-800 divide-y divide-zinc-800" data-testid="help-article-list">
        {articles.map((a) => {
          const isOpen = open === a.slug;
          return (
            <div key={a.slug} className="bg-zinc-900/20">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : a.slug)}
                data-testid={`help-article-item-${a.slug}`}
                aria-expanded={isOpen}
                className="w-full text-left px-5 md:px-8 py-5 flex items-start gap-4 hover:bg-zinc-900/60 transition-colors duration-150"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold tracking-tight">{a.title}</div>
                  <div className="text-xs text-zinc-500 mt-1">{a.summary}</div>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-zinc-500 shrink-0 mt-1 transition-transform duration-200 ${isOpen ? "rotate-180 text-blue-400" : ""}`}
                />
              </button>
              {isOpen && (
                <div
                  className="px-5 md:px-8 pb-6 space-y-4 animate-in fade-in slide-in-from-top-1 duration-200"
                  data-testid={`help-article-body-${a.slug}`}
                >
                  {(a.sections || []).map((s) => (
                    <div key={s.heading} className="border-l-2 border-zinc-800 pl-4">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5">
                        {s.heading}
                      </div>
                      <p className="text-sm text-zinc-300 leading-relaxed">{s.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="border border-blue-500/25 bg-blue-500/5 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <LifeBuoy className="w-5 h-5 text-blue-400 shrink-0" />
        <div className="flex-1">
          <div className="text-white font-semibold tracking-tight">Still stuck?</div>
          <div className="text-xs text-zinc-400 mt-1">
            Report the problem with the video attached and you'll get a ticket
            you can follow.
          </div>
        </div>
        <button
          type="button"
          onClick={onContactSupport}
          data-testid="help-center-contact-btn"
          className="text-xs font-mono uppercase tracking-widest bg-white text-zinc-950 hover:bg-zinc-200 px-4 py-2.5 transition-colors shrink-0"
        >
          Report a problem
        </button>
      </div>
    </div>
  );
};
